import { useEffect, useRef, useState } from "react";
import Button from "../../components/button/Button";
import { Spiner } from "../../components/spinner/Spiner";
import { auth } from "../../firebase";
import companiesQuery from "../../firestore/Companies.query";
import { convertDateToString } from "../../utility/ConvertUtily";
import { VideoContents } from "../../model/VideoContents";
import { VideoContentsAddModal } from "./VideoContentsAddModal";
import { VideoContentsDeleteModal } from "./VideoContentsDeleteModal";
import TableList, { GridCol, GridRow } from "../../components/table/TableList";
import { CompanySelectModal } from "./CompanySelectModal";
import { Company } from "../../model/Companies";
import { useDispatch } from "react-redux";
import { AlertValue, alertOpen } from "../../features/alert.slice";

export const VideoContentsPage = () => {

  // ログイン中の団体管理者情報
  const currentUser = auth.currentUser;

  const isDisabled = useRef(false);

  const dispatch = useDispatch();

  const [isOpen, setIsOpen] = useState(false);
  const [isDeleteOpen, setIsDeleteOpen] = useState(false);
  const [isSelectOpen, setIsSelectOpen] = useState(false);

  const [isLoad, setIsLoad] = useState(true);

  const [deleteVideoContents, setDeleteVideoContents] = useState<VideoContents | undefined>();

  const [contentsRowList, setContentsRowList] = useState<GridRow[]>([]);
  const [contentsData, setContentsData] = useState<VideoContents>();
  const [selectCompany, setSelectCompany] = useState<string[]>();
  const [companySelectDialogCheckedIdList, setCompanySelectDialogCheckedIdList] = useState<string[]>();
  const [companies, setCompanies] = useState<Company[]>([]);
  const [filteredCompanies, setFilteredCompanies] = useState<Company[]>(companies);
  const [companiesRows, setCompaniesRows] = useState<GridRow[]>([]);
  const [selectRows, setSelectRows] = useState<GridRow[]>([]);

  useEffect(() => {
    const f = async () => {
      // 団体一覧取得
      const companies = await companiesQuery.getCompaniesList();
      const baseFilteredCompanies = companies.filter((company) =>
        company.name !== undefined
        && company.name !== ''
        && !company.deleteFlg
      );
      setCompanies(baseFilteredCompanies);
      setFilteredCompanies(baseFilteredCompanies);

      // コンテンツ一覧取得
      const videoContentsList = await companiesQuery.getVideoContentsList();

      const contentsRowList: GridRow[] = videoContentsList.map((contents) => {
        return {
          rowData: {
            title: (<div className={"sm:whitespace-normal"}>{contents.title ?? ''}</div>),
            type: contents.type,
            createUserType: contents.createUserType === 'admin' ? '運営' : '団体',
            author: contents.createUser,
            explanation: contents.explanation,
            createAt: (<div className={"whitespace-nowrap"}>{convertDateToString(contents.createAt)}</div>),
            actions: (contents.createUserType === 'admin') ? (
              <div className={'flex space-x-2'}>
                <Button
                  className={"whitespace-nowrap"}
                  color={'indigo'}
                  children={"編集"}
                  onClick={() => {
                    onEdit(contents);
                    setSelectCompany(contents.companies);
                    setCompanySelectDialogCheckedIdList(contents.companies)
                  }}
                />
                <Button
                  className={"whitespace-nowrap"}
                  color={'red'}
                  children={"削除"}
                  onClick={() => {
                    setDeleteVideoContents(contents);
                    deleteToggle();
                  }}
                />
              </div>
            ) : (<></>)
          },
          sortableRowData: {
            title: contents.title ?? '',
            createUserType: contents.createUserType === 'admin',
            createAt: convertDateToString(contents.createAt)
          },
          style: 'sm:whitespace-normal'
        }
      });
      setContentsRowList(contentsRowList);

      setIsLoad(false);
    };
    f();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, isDeleteOpen]);

  const contentsListColumns: GridCol[] = [
    {
      field: "title",
      headerName: "動画名",
      style: "w-56",
    },
    {
      field: "type",
      headerName: "動画タイプ",
      style: "w-1/12",
    },
    {
      field: "createUserType",
      headerName: "作成元",
      style: "w-1/12"
    },
    {
      field: "author",
      headerName: "登録者",
      style: "w-2/12",
    },
    {
      field: "explanation",
      headerName: "説明",
      style: "w-64",
    },
    {
      field: "createAt",
      headerName: "登録日",
      style: "w-1/12",
    },
    {
      field: "actions",
      headerName: "",
      style: "w-2/12",
      sort: false
    },
  ];

  // 動画コンテンツ登録・編集モーダル開閉
  const onEdit = (videoContents: VideoContents | undefined) => {
    setContentsData(videoContents);
    toggle();
  }

  const toggle = () => {
    setIsOpen(!isOpen);
    setIsLoad(!isLoad);
    if(!isOpen){
      setSelectCompany([]);
    }
  }
  // 動画コンテンツ登録
  const onSubmit = async (data: VideoContents) => {
    if(!isDisabled.current){
      try {
        isDisabled.current = true;
        setIsLoad(true);
        data.companies = selectCompany;
        data.createAt = new Date();
        data.createUser = currentUser?.email ?? '';
        data.createUserType = 'admin';
        const result = await companiesQuery.upsertVideoContent(data);
        if(result.success){
          // 団体に登録されている自身の動画コンテンツを一旦削除
          const delResult = await companiesQuery.deleteVideoContentsIntoCompanies(data.id);
          if(!delResult.success) {
            throw new Error();
          }
          // 選択された団体に動画コンテンツを登録
          const setResult = await companiesQuery.setVideoContentsIntoCompanies(result.data);
          if(!setResult.success) {
            throw new Error();
          }
        } else {
          throw new Error();
        }
        dispatch(alertOpen(AlertValue("動画コンテンツを保存しました。", "success")));
        toggle();
        isDisabled.current = false;
      } catch (error: any) {
        console.error(error);
        dispatch(alertOpen(AlertValue("保存に失敗しました。", "danger")));
        isDisabled.current = false;
      }
    }
  }
  // 動画コンテンツ削除ダイアログ開閉
  const deleteToggle = () => {
    setIsDeleteOpen(!isDeleteOpen);
    setIsLoad(!isLoad);
  };
  // 動画コンテンツ削除
  const onContentsDelete = async (videoContents?: VideoContents) => {
    if(!isDisabled.current){
      try{
        isDisabled.current = true;
        setIsLoad(true);
        if(videoContents === undefined){
          deleteToggle();
          isDisabled.current = false;
          return;
        };
        await companiesQuery.deleteVideoContents(videoContents.id);
        // 団体に登録している動画コンテンツを削除
        const delResult = await companiesQuery.deleteVideoContentsIntoCompanies(videoContents.id);
        if(!delResult.success) {
          throw new Error();
        }
        dispatch(alertOpen(AlertValue("動画コンテンツを削除しました。", "success")));
        deleteToggle();
        isDisabled.current = false;
      } catch (err: any) {
        console.error(err);
        dispatch(alertOpen(AlertValue("削除に失敗しました。", "danger")));
        isDisabled.current = false;
      }
    }
  };

  // 動画コンテンツ登録団体一覧のカラム
  const selectColumn: GridCol[] = [
    {
      field: 'displayId',
      headerName: '団体番号',
      style: ''
    },
    {
      field: 'name',
      headerName: '団体名',
      style: ''
    },
    {
      field: 'userCount',
      headerName: '契約数',
      style: ''
    },
  ];

  // 動画コンテンツ登録団体一覧のカラム
  const companiesColumns: GridCol[] = [
    {
      field: "check",
      headerName: '',
      style: ""
    },
    ...selectColumn
  ];

  // 動画コンテンツ登録団体一覧データ作成
  useEffect(() => {
    const companiesRows: GridRow[] = filteredCompanies.map((company) => {
      return {
        rowData: {
          check: (
            <>
              <input
                id={`company-${company.id}`}
                aria-describedby="token-description"
                name={`company-${company.id}`}
                type="checkbox"
                className="h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                data-id={company.id}
                data-category={"company"}
                onChange={onSelectDoc}
                checked={companySelectDialogCheckedIdList?.includes(company.id)}
              />
            </>
          ),
          displayId: company.displayId,
          name: company.name,
          userCount: company.usersCount,
        },
        sortableRowData: {
          check: companySelectDialogCheckedIdList?.includes(company.id) ?? false
        }
      }
    });
    setCompaniesRows(companiesRows);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  },[contentsData?.companies, filteredCompanies, companySelectDialogCheckedIdList]);

  const onSelectDoc = (event: React.ChangeEvent<HTMLInputElement>) => {
    const selectedId = event.currentTarget.getAttribute("data-id") ?? '';
    if(event.target.checked){
      companySelectDialogCheckedIdList?.push(selectedId);
      setCompanySelectDialogCheckedIdList([...new Set(companySelectDialogCheckedIdList)]);
    } else {
      const filter = companySelectDialogCheckedIdList?.filter((checkedId: string) => checkedId !== selectedId);
      setCompanySelectDialogCheckedIdList([...new Set(filter)]);
    }
  };

  // 動画コンテンツ登録団体選択モーダル戻り値
  const onCompanySubmit = (event: React.FormEvent<HTMLFormElement>) => {
    setSelectCompany(companySelectDialogCheckedIdList);
    selectToggle();
  };

  useEffect(() => {
    const selectedCompanies = companies.filter((company) => selectCompany?.includes(company.id));
    const selectRows = selectedCompanies.map((company): GridRow => {
      return {
        rowData: {
          displayId: company.displayId,
          name: company.name,
          userCount: company.usersCount,
        }
      }
    });
    setSelectRows(selectRows);
  },[companies, selectCompany]);

  // 動画コンテンツ登録団体検索
  const onSearch = (e: any) => {
    const regex = new RegExp(e.target.value);
    if(e.target.value.length > 1){
      const filteredCompanies = companies.filter((company) =>
        company.name?.match(regex)
        || company.nameKana?.match(regex)
        || company.displayId?.match(regex)
      );
      setFilteredCompanies(filteredCompanies);
    } else {
      setFilteredCompanies(companies);
    }
  };

  // 動画コンテンツ登録団体モーダル開閉
  const selectToggle = () => {
    setIsSelectOpen(!isSelectOpen);
    setFilteredCompanies(companies);
  };

  return (
    <div className="h-full w-min min-w-full">
      {isLoad ? (<Spiner/>) : (<>
        <div className="flex items-center space-x-4 p-6 justify-between">
          <h2 className="flex text-xl whitespace-nowrap font-semibold text-gray-900">
            動画コンテンツ一覧
          </h2>
          <div className={"flex space-x-2 items-center"}>
            <Button
              size={'sm'}
              onClick={() => onEdit(undefined)}
              children={"動画コンテンツ新規登録"}
            />
          </div>
        </div>
        <div className={"px-6"}>
          <TableList
            header
            gridCol={contentsListColumns}
            gridRow={contentsRowList}
            limit={50}
          />
          {/* 動画コンテンツ登録・編集モーダル */}
          <VideoContentsAddModal
            children={undefined}
            open={isOpen}
            toggle={toggle}
            selectToggle={selectToggle}
            closeOnClickOutside={false}
            data={contentsData}
            onContentsAddSubmit={onSubmit}
            isLoad={isLoad}
            selectColumn={selectColumn}
            selectRows={selectRows}
          />
          {/* 動画コンテンツ削除確認モーダル */}
          <VideoContentsDeleteModal
            children={undefined}
            open={isDeleteOpen}
            toggle={deleteToggle}
            closeOnClickOutside={false}
            videoContents={deleteVideoContents}
            handle={() => onContentsDelete(deleteVideoContents)}
            isLoad={isLoad}
          />
          {/* 団体選択モーダル */}
          <CompanySelectModal
            children={undefined}
            open={isSelectOpen}
            toggle={selectToggle}
            closeOnClickOutside={false}
            onCompanySubmit={onCompanySubmit}
            columns={companiesColumns}
            rowList={companiesRows}
            onSearch={onSearch}
          />
        </div>
      </>)}
    </div>
  );
}